import React, { Fragment, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import Button from "components/Button";
import {
  StyledForm,
  FormGroup,
  UpgradeText,
  BrandingFormSection,
  UpgradeButtonLinkContainer,
  UpgradeButtonLink,
} from "./styledComponents";
import { BrandFormField, PhotoFilterPreview } from "./components";
import DevicePreview, {
  GalleryPreview,
} from "../../../components/DevicePreview";
import { colors } from "globalStyles";
import { isObject, isSafeHttpUrl } from "utils/helpers";
import UpgradeCta from "containers/UpgradeCta";
import Divider from "components/Divider";
import { Controller, useWatch } from "react-hook-form";
import { planLimitsForUser } from "../planLimits";

const getFilePath = (file) => {
  if (isObject(file)) {
    const fileObj = file[0] || file;
    try {
      return fileObj ? window.URL.createObjectURL(fileObj) : "";
    } catch (err) {
      return fileObj ? fileObj.name : "";
    }
  }

  return file;
};

const validateImage = (value, width, height) => {
  return new Promise((resolve, reject) => {
    if (!value) resolve();
    const img = new Image();
    const _URL = window.URL || window.webkitURL;
    var objectUrl = _URL.createObjectURL(value.target.files[0]);
    img.onload = function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (width === img.naturalWidth && height === img.naturalHeight) resolve();
      else reject();
    };
    img.src = objectUrl;
  });
};

const formOptions = [
  {
    fields: () => [
      {
        name: "big_logo_image_url",
        header: "App Logo",
        description: "Upload a .PNG file, exactly 1300px wide by 800px tall",
        label: "App Logo File",
        type: "file",
        enableRemoveIcon: true,
        placeholder: "upload a .PNG file, exactly 1300px wide by 800px tall",
        validateFnc: (value) => validateImage(value, 1300, 800),
        validateMsg:
          "The image does not match the required resolution (1300px wide by 800px tall)",
      },
      {
        name: "app_background_color",
        header: "App Background",
        description: "Pick a color, any color",
        disable_description:
          "Remove uploaded image to switch to basic color background.",
        label: "App Background",
        type: "color",
        colorPreviewOnLeft: true,
        enableRemoveIcon: true,
        placeholder: "enter hex number or pick a color form drop-down",
        disabled: (brandForm) => (brandForm["overlay_image"] ? true : false),
        // The iPad background image is on every plan.
        subFields: [
          {
            name: "overlay_image",
            label: "App Background File",
            description:
              "or upload a .PNG file, exactly 1536px wide by 2048px tall",
            placeholder:
              "upload a .PNG file, exactly 1536px wide by 2048px tall",
            type: "file",
            enableRemoveIcon: true,
            validateFnc: (value) => validateImage(value, 1536, 2048),
            validateMsg:
              "The image does not match the required resolution (1536px wide by 2048px tall)",
          },
        ],
      },
    ],
    previewComponent: (brandForm) => {
      return (
        <DevicePreview
          type="pro"
          logoImage={getFilePath(brandForm["big_logo_image_url"])}
          overlayImage={getFilePath(brandForm["overlay_image"])}
          backgroundColor={brandForm["app_background_color"]}
        />
      );
    },
  },
  {
    fields: () => [
      {
        name: "photo_filters",
        header: "Photo Filters",
        description: "Upload .PNG files, exactly 960px wide by 1280px tall.",
        label: "Photo Filter Files",
        type: "file",
        placeholder: "upload .PNG files, exactly 960px wide by 1280px tall",
        isMultiSelect: true,
        validateFnc: (value) => validateImage(value, 960, 1280),
        validateMsg:
          "The image does not match the required resolution (960px wide by 1280px tall)",
      },
    ],
    previewComponent: (brandForm) => {
      var photos = brandForm["photo_filters"];
      const firstLine = brandForm["first_line_text"];
      const secondLine = brandForm["second_line_text"];
      const withGraphic = brandForm["with_photo_filter_graphic"];
      const withFilter = brandForm["with_photo_filter"];
      // photos[0].selected = true;
      if (photos && Array.isArray(photos)) {
        if (
          photos.length > 0 &&
          photos.filter((x) => x && x.selected).length === 0
        ) {
          photos[0].selected = true;
        }
        photos = photos.filter((x) => x && x.selected);

        photos = photos
          ? photos.map((item) =>
              typeof item.file === "string" ? item.file : getFilePath(item.file)
            )
          : null;
      }

      return (
        <PhotoFilterPreview
          photoOptions="4pose"
          photoFilters={photos}
          firstLine={firstLine}
          secondLine={secondLine}
          withGraphic={withGraphic}
          withFilter={withFilter}
        />
      );
    },
  },
  {
    fields: (userType) => [
      {
        name: "gallery_banner",
        header: "Gallery Logo",
        description:
          "Upload a .PNG file, exactly 1300px wide by 800px tall. Your App Logo is the default.",
        label: "Gallery Logo File",
        placeholder: "upload .PNG files, exactly 1300px wide by 800px tall",
        validateFnc: (value) => validateImage(value, 1300, 800),
        validateMsg:
          "The image does not match the required resolution (1300px wide by 800px tall)",
        type: "file",
      },
      userType === "lite"
        ? {
            type: "upgrade",
            description: "Upgrade to add Copy and CTA button",
          }
        : {
            name: "header",
            header: "Gallery Copy",
            description:
              "Enter a headline and subheadline copy to add to your gallery.",
            placeholder: "Thank you for visiting our photo booth!",
            label: "gallery headline",
            type: "text",
            multiline: 5,
            subFields: [
              {
                name: "sub_header",
                label: "gallery sub-headline",
                placeholder: "We hope you enjoy your photos.",
                type: "text",
                multiline: 5,
              },
              !["pro", "enterprise_admin", "super_admin"].includes(userType)
                ? {
                    name: "brand_cta_button_nedd_upgrade",
                    type: "upgrade",
                    description: "Upgrade to add CTA button",
                  }
                : {
                    name: "gallery_cta_text",
                    label: "CTA Button Text",
                    placeholder: "Get 10% Off Your Next Purchase",
                    type: "text",
                  },
              ["lite", "plus"].includes(userType)
                ? null
                : {
                    name: "gallery_cta_link",
                    label: "CTA Button Url",
                    placeholder: "https://www.movebooth.com/",
                    type: "text",
                    // Block storing a non-http(s) CTA URL (e.g. javascript:/data:)
                    // — react-hook-form will not submit the form while this
                    // fails, so the payload never reaches the server.
                    rules: {
                      validate: (value) =>
                        !value ||
                        isSafeHttpUrl(value) ||
                        "Enter a valid http(s):// URL",
                    },
                  },
            ],
          },
      {
        name: "gallery_color",
        header: "Gallery Background",
        description: "Pick a color, any color.",
        label: "Gallery Background",
        type: "color",
        placeholder: "enter hex number or pick a color from drop-down",
        colorPreviewOnLeft: true,
      },
    ],
    previewComponent: (brandForm) => (
      <GalleryPreview
        logoImage={
          getFilePath(brandForm["gallery_banner"]) ||
          getFilePath(brandForm["big_logo_image_url"])
        }
        backgroundColor={brandForm["gallery_color"] || colors.MENU}
        headline={brandForm["header"]}
        subHeadline={brandForm["sub_header"]}
        buttonText={brandForm["gallery_cta_text"]}
      />
    ),
    marginBottom: 48,
  },
];

const BrandingForm = ({
  nextTab,
  openColorPickerModal,
  edit,
  submitting,
  onSubmit,
  permissionRole,
  form,
  brandForm,
  user,
}) => {
  const { control, handleSubmit, getValues, setValue } = form;
  // What the plan includes, as rails sends it (one source: rails' User#plan_limits).
  const planLimits = planLimitsForUser(user);
  const photoFiltersLimited = planLimits.photo_filters !== null;
  const galleryLogoIncluded = planLimits.gallery_banner;

  // Records the id of a photo filter the user actually deleted, on a form field
  // of its own (`deleted_photo_filter_ids`) that rides along to the API layer.
  //
  // A sibling field rather than a marker inside `photo_filters`: every existing
  // photo_filters path (name-matching rebuilds in listChanged, the wholesale
  // rebuild in listSelectChanged, the saga's empty/`some(x => x.file)`
  // branches, and this form's own preview, which mutates entry 0) would each
  // have to learn to step around such a marker. Keeping the intent beside the
  // list leaves all of them semantically unchanged.
  //
  // Reads through getValues rather than a render-time closure so a second
  // deletion in the same session sees the first one.
  const recordDeletedFilterId = useCallback(
    (id) => {
      if (id === undefined || id === null) return;
      const current = getValues("deleted_photo_filter_ids");
      const next = Array.isArray(current) ? current : [];
      if (next.some((existing) => String(existing) === String(id))) return;
      setValue("deleted_photo_filter_ids", [...next, id]);
    },
    [getValues, setValue],
  );

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      {formOptions.map((options, idx) => {
        return (
          <div key={idx}>
            <FormGroup className="branding-form-group">
              <BrandingFormSection>
                <div className="left-side">
                  {options.fields(permissionRole).map((fieldProps, idx) => {
                    return typeof fieldProps.name !== "undefined" ? (
                      <div key={idx}>
                        {/* A plan without a gallery logo (Lite) gets no upload control;
                            rails refuses one, and the upgrade note below stands in.
                            A logo the channel already has stays shown with its
                            remove control (rails lets any plan clear it). */}
                        {!(
                          fieldProps.name === "gallery_banner" &&
                          !galleryLogoIncluded &&
                          !brandForm["gallery_banner"]
                        ) && (
                          <Controller
                            control={control}
                            name={fieldProps.name}
                            render={({ field: { ref, ...fieldRest } }) => (
                              <BrandFormField
                                {...fieldProps}
                                brandForm={brandForm}
                                openColorPickerModal={openColorPickerModal}
                                permissionRole={permissionRole}
                                enableRemoveIcon={
                                  fieldProps.name === "gallery_banner"
                                    ? brandForm["gallery_banner"]
                                    : fieldProps.enableRemoveIcon
                                }
                                control={control}
                                field={fieldRest}
                                {...(fieldProps.name === "photo_filters"
                                  ? {
                                      recordDeletedFilterId,
                                      photoFilterLimit: planLimits.photo_filters,
                                    }
                                  : {})}
                                {...(fieldProps.name === "gallery_banner"
                                  ? { uploadAllowed: galleryLogoIncluded }
                                  : {})}
                                {...fieldRest}
                              />
                            )}
                          />
                        )}
                        {fieldProps.name === "photo_filters" &&
                          photoFiltersLimited && (
                            <UpgradeButtonLinkContainer>
                              <UpgradeCta as={UpgradeButtonLink}>
                                Upgrade to give users multiple filters to choose
                                from
                              </UpgradeCta>
                            </UpgradeButtonLinkContainer>
                          )}
                        {fieldProps.name === "gallery_banner" &&
                          !galleryLogoIncluded && (
                            <UpgradeButtonLinkContainer>
                              <UpgradeCta as={UpgradeButtonLink}>
                                {permissionRole === "lite"
                                  ? "Upgrade to add a Gallery Logo, Copy and CTA Button"
                                  : "Upgrade to add a Gallery Logo"}
                              </UpgradeCta>
                            </UpgradeButtonLinkContainer>
                          )}
                      </div>
                    ) : (
                      <Fragment key={idx}></Fragment>
                    );
                  })}
                </div>
                <div className="right-side">
                  {options.previewComponent(brandForm)}
                </div>
              </BrandingFormSection>
              <Divider marginBottom={options.marginBottom || 20}></Divider>
            </FormGroup>
          </div>
        );
      })}

      <UpgradeText className="left-pos pdd-20">
        Looking for more? Send us a suggestion.
      </UpgradeText>
      <div className="channel-forms__group">
        {edit ? (
          <Button type="submit" disabled={submitting}>
            Save
          </Button>
        ) : (
          <Button type="button" onClick={() => nextTab()}>
            Save & Continue
          </Button>
        )}
      </div>
    </StyledForm>
  );
};

export default connect(
  (state) => ({
    user: state.app.user,
  }),
  null
)(BrandingForm);
